import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface Match {
  id: string;
  sport: string;
  competition: string;
  home_team: string;
  away_team: string;
  match_date: string;
  match_time: string;
  channel: string | null;
  home_logo_url: string | null;
  away_logo_url: string | null;
  phase: string | null;
  group_name: string | null;
  stadium: string | null;
  stadium_city: string | null;
  created_at: string;
}

interface Filters {
  sport?: string;
  competition?: string;
  dateRange?: 'today' | 'week' | 'all';
  search?: string;
}

export function useMatches(filters?: Filters) {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState<string[]>([]);

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      let query = supabase
        .from('matches')
        .select('*')
        .gte('match_date', today)
        .order('match_date')
        .order('match_time')
        .limit(300);

      if (filters?.sport) query = query.ilike('sport', filters.sport);
      if (filters?.competition) query = query.eq('competition', filters.competition);

      if (filters?.dateRange === 'today') {
        query = query.eq('match_date', today);
      } else if (filters?.dateRange === 'week') {
        const end = new Date();
        end.setDate(end.getDate() + 7);
        query = query.lte('match_date', end.toISOString().split('T')[0]);
      }

      const { data, error } = await query;
      if (error) throw error;

      let result = data || [];

      if (filters?.search) {
        const s = filters.search.toLowerCase();
        result = result.filter(m =>
          m.home_team.toLowerCase().includes(s) ||
          m.away_team.toLowerCase().includes(s) ||
          (m.competition || '').toLowerCase().includes(s)
        );
      }

      setMatches(result);

      // Fetch all competitions (unfiltered) so dropdown stays full
      const { data: allMatches } = await supabase
        .from('matches')
        .select('competition')
        .gte('match_date', today)
        .limit(500);
      const compSet = new Set((allMatches || []).map(m => m.competition).filter(Boolean));
      const comps = Array.from(compSet).sort();
      setCompetitions(comps);
    } catch (err) {
      console.error('Fetch matches error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters?.sport, filters?.competition, filters?.dateRange, filters?.search]);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  const createMatch = async (match: Partial<Match>) => {
    const { error } = await supabase.from('matches').insert(match);
    if (error) throw error;
    await fetchMatches();
  };

  const updateMatch = async (id: string, updates: Partial<Match>) => {
    const { error } = await supabase.from('matches').update(updates).eq('id', id);
    if (error) throw error;
    await fetchMatches();
  };

  const deleteMatch = async (id: string) => {
    await supabase.from('diffusions').delete().eq('match_id', id);
    const { error } = await supabase.from('matches').delete().eq('id', id);
    if (error) throw error;
    await fetchMatches();
  };

  return { matches, loading, competitions, refetch: fetchMatches, createMatch, updateMatch, deleteMatch };
}
